import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Terrain, VerificationStatus } from './types.ts';

export type Lang = 'ro' | 'en';

const STRINGS = {
  ro: {
    title: 'Semimaratoane trail România',
    subtitle: 'Calendarul 2026, verificat automat față de sursele publice',
    searchPlaceholder: 'Caută după cursă, localitate, județ, masiv…',
    month: 'Luna',
    county: 'Județ',
    terrain: 'Teren',
    status: 'Stare verificare',
    distance: 'Distanță semimaraton',
    all: 'toate',
    reset: 'Resetează filtrele',
    races: 'curse',
    raceOne: 'cursă',
    noResults: 'Nicio cursă nu corespunde filtrelor.',
    verifyNow: 'Verifică sursele acum',
    verifying: 'Se verifică…',
    lastChecked: 'Ultima verificare',
    never: 'niciodată',
    sourcesChecked: 'surse citite',
    elevation: 'diferență de nivel',
    distances: 'Distanțe',
    evidence: 'Dovezi din surse',
    matchedOn: 'găsit după',
    datesOnPage: 'date găsite lângă nume',
    noDatesOnPage: 'nicio dată lângă nume',
    openSource: 'Deschide sursa',
    close: 'Închide',
    sourcesTitle: 'Surse monitorizate',
    sourcesIntro:
      'Serverul citește aceste pagini, caută numele fiecărei curse și compară data de lângă el cu data din calendar.',
    citedBy: 'curse citate',
    fetchOk: 'citită',
    fetchFail: 'inaccesibilă',
    chars: 'caractere',
    disclaimer:
      'Datele sunt preluate din calendare publice și pot fi modificate de organizatori. Confirmă întotdeauna pe pagina oficială a cursei înainte de a te înscrie.',
    approxDate: 'dată aproximativă',
    itra: 'punctaj ITRA/UTMB',
    theme: 'Temă',
    monthNames: [
      'ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie',
      'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie',
    ],
    statusLabels: {
      confirmed: 'confirmată',
      'date-mismatch': 'dată diferită',
      'found-no-date': 'găsită, fără dată',
      'not-found': 'negăsită',
      unreachable: 'sursă inaccesibilă',
      unverified: 'neverificată',
    } satisfies Record<VerificationStatus, string>,
    statusHelp: {
      confirmed: 'Numele cursei apare pe sursă, iar data de lângă el se potrivește.',
      'date-mismatch': 'Numele apare, dar data de pe sursă diferă de cea din calendar.',
      'found-no-date': 'Numele apare pe sursă, dar nu am găsit o dată lizibilă lângă el.',
      'not-found': 'Numele nu apare pe paginile-sursă citite acum.',
      unreachable: 'Nu am putut încărca sursele acestei curse.',
      unverified: 'Nu a rulat încă nicio verificare pentru această cursă.',
    } satisfies Record<VerificationStatus, string>,
    terrainLabels: {
      munte: 'munte',
      deal: 'deal',
      padure: 'pădure',
      chei: 'chei',
      podgorie: 'podgorie',
    } satisfies Record<Terrain, string>,
  },
  en: {
    title: 'Romanian trail half marathons',
    subtitle: 'The 2026 calendar, checked automatically against its public sources',
    searchPlaceholder: 'Search race, town, county, massif…',
    month: 'Month',
    county: 'County',
    terrain: 'Terrain',
    status: 'Check status',
    distance: 'Half distance',
    all: 'all',
    reset: 'Reset filters',
    races: 'races',
    raceOne: 'race',
    noResults: 'No race matches these filters.',
    verifyNow: 'Re-check the sources',
    verifying: 'Checking…',
    lastChecked: 'Last checked',
    never: 'never',
    sourcesChecked: 'sources read',
    elevation: 'elevation gain',
    distances: 'Distances',
    evidence: 'Evidence from sources',
    matchedOn: 'matched on',
    datesOnPage: 'dates found next to the name',
    noDatesOnPage: 'no date next to the name',
    openSource: 'Open source page',
    close: 'Close',
    sourcesTitle: 'Monitored sources',
    sourcesIntro:
      'The server reads these pages, looks for each race name, and compares the date next to it with the one in the calendar.',
    citedBy: 'races cited',
    fetchOk: 'read',
    fetchFail: 'unreachable',
    chars: 'characters',
    disclaimer:
      'Dates come from public calendars and organisers do change them. Always confirm on the official race page before entering.',
    approxDate: 'approximate date',
    itra: 'ITRA/UTMB points',
    theme: 'Theme',
    monthNames: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    statusLabels: {
      confirmed: 'confirmed',
      'date-mismatch': 'date differs',
      'found-no-date': 'found, no date',
      'not-found': 'not found',
      unreachable: 'source unreachable',
      unverified: 'not checked yet',
    } satisfies Record<VerificationStatus, string>,
    statusHelp: {
      confirmed: 'The race name is on the source page and the date beside it matches.',
      'date-mismatch': 'The name is there, but the date on the source disagrees with ours.',
      'found-no-date': 'The name is on the page, but no readable date sits close to it.',
      'not-found': 'The name does not appear on the source pages as read just now.',
      unreachable: 'None of this race’s sources could be loaded.',
      unverified: 'No verification pass has covered this race yet.',
    } satisfies Record<VerificationStatus, string>,
    terrainLabels: {
      munte: 'mountain',
      deal: 'hills',
      padure: 'forest',
      chei: 'gorge',
      podgorie: 'vineyard',
    } satisfies Record<Terrain, string>,
  },
} as const;

export type Strings = (typeof STRINGS)['ro'];

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Strings;
  formatDate: (startISO: string, endISO?: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem('trail-lang');
    if (stored === 'ro' || stored === 'en') return stored;
    return navigator.language.toLowerCase().startsWith('ro') ? 'ro' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('trail-lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);

  const value = useMemo<I18nValue>(() => {
    const t = STRINGS[lang] as unknown as Strings;
    const formatDate = (startISO: string, endISO?: string) => {
      const start = new Date(`${startISO}T00:00:00Z`);
      const startDay = start.getUTCDate();
      const startMonth = t.monthNames[start.getUTCMonth()] ?? '';
      if (!endISO || endISO === startISO) {
        return lang === 'ro'
          ? `${startDay} ${startMonth}`
          : `${startMonth} ${startDay}`;
      }
      const end = new Date(`${endISO}T00:00:00Z`);
      const endDay = end.getUTCDate();
      const endMonth = t.monthNames[end.getUTCMonth()] ?? '';
      if (start.getUTCMonth() === end.getUTCMonth()) {
        return lang === 'ro'
          ? `${startDay}–${endDay} ${startMonth}`
          : `${startMonth} ${startDay}–${endDay}`;
      }
      return lang === 'ro'
        ? `${startDay} ${startMonth} – ${endDay} ${endMonth}`
        : `${startMonth} ${startDay} – ${endMonth} ${endDay}`;
    };
    return { lang, setLang, t, formatDate };
  }, [lang, setLang]);

  return <I18nContext value={value}>{children}</I18nContext>;
}

export function useI18n(): I18nValue {
  const value = use(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}
